// cleanup_hoc_to_hooks_GENERICS
// cleanup_convert_to_typescript_file_GENERICS
// cleanup_remove_external_libraries_GENERICS

import React, { useContext, useEffect, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEqual, template, uniqueId } from 'lodash';

import Alert from '@glu/alerts-react';
import { Button, TERTIARY } from '@glu/buttons-react';

import { fetchAllWidgets, fetchWorkspaceDefaults } from 'api/workspace/service';
import { logEvent } from 'api/audits/service';
import Widget from 'components/widget/Widget';
import manifest from 'components/manifest';
import useLocale from 'components/useLocale';
import { useAppConfig } from 'components/appConfig/AppConfig';
import UserPreferencesContext from 'components/withUserPreferences/UserPreferencesContext';
import { ALERTVARIATIONS, STORAGE_KEYS, WORKSPACES, WORKSPACE_ID } from 'constants.js';
import updateWhere from 'utils/updateWhere';

import WorkspaceContext from './WorkspaceContext';
import WorkspaceGroup from './WorkspaceGroup';
import WorkspaceRow from './WorkspaceRow';
import WidgetContext from './WidgetContext';
import useWebStyles from './workspace.styles';
import reducer, { ACTIONS } from './Workspace.reducer';
import useNativeStyles from './workspace.native.styles';
import { isWidgetLocked } from './utils';

const { HIDE_PASSWORD_EXPIRATION_ALERT } = STORAGE_KEYS;
const { CHANGEPASSWORD } = WORKSPACES;
const { DASHBOARD } = WORKSPACE_ID;
const { INFO, WARNING } = ALERTVARIATIONS;

export const Workspace = ({ showMissing = false, workspaceId, workspaceName }) => {
  const history = useHistory();
  const locale = useLocale();
  const [
    {
      allowedWidgets,
      errorMessage,
      lockedDefaults,
      showPasswordExpirationAlert,
      unlockedDefaults
    },
    dispatch
  ] = useReducer(reducer, {
    allowedWidgets: [],
    errorMessage: '',
    lockedDefaults: [],
    showPasswordExpirationAlert: false,
    unlockedDefaults: []
  });
  const { passwordExpirationDays, showPasswordExpirationWarning } = useSelector(
    (state) => state?.auth?.user
  );
  const {
    get: getUserPreferences,
    getLocal,
    set: setUserPreferences,
    setLocal
  } = useContext(UserPreferencesContext);
  const userPrefs = getLocal(workspaceId) || unlockedDefaults;
  const missingDefaults = unlockedDefaults.filter(
    ({ isRemovable, widgetCode }) =>
      !isRemovable &&
      !userPrefs.find((widget) => widget.isDefault && widget.widgetCode === widgetCode)
  );
  const { isWeb } = useAppConfig();
  const webStyles = useWebStyles();
  const nativePageStyles = useNativeStyles();
  const {
    changePasswordStyles,
    locked,
    passwordExpirationAlertStyles,
    workspace: workspaceStyles
  } = isWeb ? webStyles : nativePageStyles;

  const handleError = (e) => {
    /* eslint-disable-next-line no-console */
    console.error(e);
    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: locale['common.server.error'] });
  };

  const handleDefaults = ([defaultWidgets, { data: allowedWidgetsToSet }]) => {
    if (!Array.isArray(defaultWidgets)) {
      handleError();
    } else {
      const mappedDefaults = defaultWidgets.filter(
        ({ widgetCode }) => manifest[widgetCode] || showMissing
      );

      const sortedDefaults = mappedDefaults
        .sort(({ rowNumber: rowA }, { rowNumber: rowB }) => rowA - rowB)
        .map((widget) => ({
          uniqueId: uniqueId(`${widget.widgetCode}_`),
          isDefault: true,
          locked: isWidgetLocked(widget),
          ...widget
        }));

      const [lockedDefaultsToSet, unlockedDefaultsToSet] = sortedDefaults.reduce(
        ([lockedArray, unlockedArray], widget) =>
          widget.locked
            ? [[...lockedArray, widget], unlockedArray]
            : [lockedArray, [...unlockedArray, widget]],
        [[], []]
      );

      // Adding uniqueId to the local userprefs
      const userPrefs = getLocal(workspaceId) || unlockedDefaultsToSet;
      const userPreferenceWidgets = userPrefs
        .filter((widget) =>
          allowedWidgetsToSet.find(({ widgetCode }) => widgetCode === widget.widgetCode)
        )
        .map((widget) => ({
          ...widget,
          uniqueId: uniqueId(`${widget.widgetCode}_`)
        }))
        .sort((a, b) => a.rowNumber - b.rowNumber);
      setLocal(workspaceId, userPreferenceWidgets);
      dispatch({
        type: ACTIONS.HANDLE_DEFAULTS,
        payload: {
          lockedDefaults: lockedDefaultsToSet,
          allowedWidgets: allowedWidgetsToSet,
          unlockedDefaults: unlockedDefaultsToSet
        }
      });
    }
  };

  const handleUpdate = (components) => {
    const userPrefs = components.map(({ props: { size, widget } }, rowNumber) => ({
      ...widget,
      rowNumber,
      size: widget.isResizable !== 0 ? size : widget.size
    }));
    setUserPreferences(workspaceId, userPrefs).catch(handleError);
  };

  const updateWidget =
    ({ uniqueId }) =>
    (newWidget) => {
      const collection = getLocal(workspaceId);
      const newUserPreferences = updateWhere(collection, { uniqueId }, newWidget);
      setLocal(workspaceId, newUserPreferences);

      const unlockedDefaultsToSet = updateWhere(unlockedDefaults, { uniqueId }, newWidget);
      const lockedDefaultsToSet = updateWhere(lockedDefaults, { uniqueId }, newWidget);

      if (
        !isEqual(unlockedDefaults, unlockedDefaultsToSet) ||
        !isEqual(lockedDefaults, lockedDefaultsToSet)
      ) {
        dispatch({
          type: ACTIONS.UPDATE_WIDGETS,
          payload: {
            unlockedDefaults: unlockedDefaultsToSet,
            lockedDefaults: lockedDefaultsToSet
          }
        });
      }
    };

  const navigateToChangePassword = (event) => {
    event.preventDefault();
    history.push({ pathname: CHANGEPASSWORD });
  };

  const hideAlert = () => {
    sessionStorage.setItem(HIDE_PASSWORD_EXPIRATION_ALERT, 'true');
    dispatch({ type: ACTIONS.SHOW_PASSWORD_EXPIRATION_ALERT, payload: false });
  };

  useEffect(() => {
    dispatch({
      type: ACTIONS.SHOW_PASSWORD_EXPIRATION_ALERT,
      payload: sessionStorage.getItem(HIDE_PASSWORD_EXPIRATION_ALERT) !== 'true'
    });
    logEvent({ subSvc: locale['common.workspace'], message: workspaceName });
    Promise.all([
      fetchWorkspaceDefaults(workspaceId),
      fetchAllWidgets(),
      getUserPreferences(workspaceId)
    ])
      .then(handleDefaults)
      .catch(handleError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className={workspaceStyles} id="content">
      {errorMessage && (
        <Alert
          className="col-xs-12"
          dataQa="warning-alert"
          id="loginFormError"
          message={errorMessage}
          onHide={() => dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: '' })}
          variant={WARNING}
        />
      )}
      {lockedDefaults.map((widget, widgetIndex) => {
        const {
          name: manifestName,
          path,
          ...props
        } = manifest[widget.widgetCode] || manifest.BLANK;
        const { name = manifestName, uniqueId } = widget;
        return (
          <div
            key={uniqueId}
            className={classnames('col-xs-12 no-gutters', locked)}
            data-qa="workspace-row-locked"
          >
            {showPasswordExpirationAlert &&
              showPasswordExpirationWarning &&
              workspaceId === DASHBOARD &&
              widgetIndex === 0 && (
                <Alert
                  autoFocus={false}
                  dataQa="password-expiration-info-alert"
                  message={
                    <div className={passwordExpirationAlertStyles}>
                      {template(locale['common.passwordExpirationAlert'])({
                        passwordExpirationDays
                      })}
                      <Button
                        className={classnames('btn btn-tertiary', changePasswordStyles)}
                        dataQa="change-password-button"
                        onClick={(event) => navigateToChangePassword(event)}
                        variant={TERTIARY}
                      >
                        {locale['common.changePassword']}
                      </Button>
                    </div>
                  }
                  onHide={hideAlert}
                  variant={INFO}
                />
              )}

            <WidgetContext.Provider value={{ widget: { widgetName: name } }}>
              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
              <Widget id={widget.widgetId} lock name={name} path={path} {...props} />
            </WidgetContext.Provider>
          </div>
        );
      })}

      <WorkspaceGroup className="col-xs-12 no-gutters" onUpdate={handleUpdate}>
        {missingDefaults
          .concat(userPrefs)
          .filter(Boolean)
          .filter((widget) =>
            allowedWidgets.find(({ widgetCode }) => widgetCode === widget.widgetCode)
          )
          .map(({ allowedSizes, isDefault, ...widget }, index) => {
            const widgetConfig = allowedWidgets.find(
              ({ widgetCode }) => widgetCode === widget.widgetCode
            );

            const defaultConfig = isDefault
              ? unlockedDefaults.find(({ widgetCode }) => widgetCode === widget.widgetCode) || {}
              : {};

            return {
              ...widgetConfig,
              ...defaultConfig,
              ...widget,
              isDefault,
              rowNumber: index,

              /** @description defaultConfig needs to override these values */
              isRemovable: defaultConfig.isRemovable || widget.isRemovable,
              isRepositionable: defaultConfig.isRepositionable || widget.isRepositionable,
              isResizable: defaultConfig.isResizable || widget.isResizable
            };
          })
          .map((widget) => {
            const {
              name: manifestName = template(locale['common.widget.notBuilt'])({
                widgetCode: widget.widgetCode
              }),
              path,
              ...props
            } = manifest[widget.widgetCode] || manifest.BLANK;
            const {
              allowedSizes: widgetSizes = '',
              isRemovable = true,
              isRepositionable = true,
              isResizable = true,
              name = manifestName,
              size: originSize = 'L',
              uniqueId
            } = widget;
            const allowedSizes = widgetSizes.split(',');

            /**
             * @const {string} size
             * @description a lot of weird stuff come back from the api.
             * This checks for all senarios.
             */
            const size =
              allowedSizes.find((size) => size === originSize) || allowedSizes[0] || originSize;

            return (
              <WorkspaceRow
                key={uniqueId}
                allowedSizes={Boolean(isResizable) && allowedSizes}
                canDrag={Boolean(isRepositionable)}
                id={uniqueId}
                name={name}
                removable={Boolean(isRemovable)}
                size={size}
                widget={widget}
              >
                <WidgetContext.Provider value={{ widget, updateWidget: updateWidget(widget) }}>
                  {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                  <Widget id={uniqueId} name={name} path={path} {...props} />
                </WidgetContext.Provider>
              </WorkspaceRow>
            );
          })}
      </WorkspaceGroup>
    </main>
  );
};

Workspace.propTypes = {
  showMissing: PropTypes.bool,
  workspaceId: PropTypes.string.isRequired,
  workspaceName: PropTypes.string.isRequired
};

export default WorkspaceContext(Workspace);
